// =============================================================================
// OUTCOME BUCKETS — single source of truth for outcome classification
// =============================================================================
// Used by:
// - OutcomeSummaryStrip (renders one cell per bucket present)
// - DailyOutcomesChart (collapses Installment+Closed+Restored→wins, etc.
//   for the 3-stack bar chart)
// - TransactionLog.outcomeColor (badge color per row)
//
// Editing this file changes outcome classification across the entire app.
// Bump lib/cacheVersion.ts changelog after any change.
// =============================================================================

export type OutcomeBucketId =
  | "closed"
  | "installment"
  | "lost"
  | "noshow"
  | "rescheduled"
  | "abandoned"
  | "restored"
  | "pending"
  | "unique"
  | "other";

export interface OutcomeBucket {
  id: OutcomeBucketId;
  label: string;
  textColorVar: string;
  badgeClass: string;
}

// Priority order — strip renders cells in this order, only when count > 0
export const BUCKET_ORDER: OutcomeBucketId[] = [
  "closed",
  "installment",
  "lost",
  "noshow",
  "rescheduled",
  "abandoned",
  "restored",
  "pending",
  "unique",
  "other",
];

// Non-hero outcomes — edge / admin states, NOT performance heroes (Mako Jun 20).
// The summary strip renders these in a separate, quieter zone below the hero
// cards. Still classified + filterable; just demoted visually. Everything NOT
// in this set is a hero card (closed, installment, lost, noshow, rescheduled,
// restored). `restored` stays a hero — it's a recovered win.
export const NON_HERO_BUCKETS: ReadonlySet<OutcomeBucketId> = new Set([
  "abandoned",
  "pending",
  "unique",
  "other",
]);

export const BUCKETS: Record<OutcomeBucketId, OutcomeBucket> = {
  closed: {
    id: "closed",
    label: "CLOSED",
    textColorVar: "var(--outcome-closed-text)",
    badgeClass: "glass-outcome-win",
  },
  installment: {
    id: "installment",
    label: "INSTALLMENT",
    textColorVar: "var(--outcome-installment-text)",
    badgeClass: "glass-outcome-win",
  },
  lost: {
    id: "lost",
    label: "LOST",
    textColorVar: "var(--outcome-lost-text)",
    badgeClass: "glass-outcome-loss",
  },
  noshow: {
    id: "noshow",
    label: "NO SHOW",
    textColorVar: "var(--outcome-noshow-text)",
    badgeClass: "glass-outcome-noshow",
  },
  rescheduled: {
    id: "rescheduled",
    label: "RESCHEDULED",
    textColorVar: "var(--outcome-lost-text)",
    badgeClass: "glass-outcome-loss",
  },
  abandoned: {
    id: "abandoned",
    label: "ABANDONED",
    textColorVar: "var(--outcome-lost-text)",
    badgeClass: "glass-outcome-loss",
  },
  restored: {
    id: "restored",
    label: "RESTORED",
    textColorVar: "var(--outcome-closed-text)",
    badgeClass: "glass-outcome-win",
  },
  // pending_close = provisional close, cash not yet collected. NOT a win
  // (Mako Jun 20: "not heroes") — split out of OTHER so it has its own number.
  pending: {
    id: "pending",
    label: "PENDING CLOSE",
    textColorVar: "var(--outcome-pending-text)",
    badgeClass: "glass-outcome-default",
  },
  // unique = bespoke/one-off outcome. Split out of OTHER for its own number.
  unique: {
    id: "unique",
    label: "UNIQUE",
    textColorVar: "var(--outcome-unique-text)",
    badgeClass: "glass-outcome-default",
  },
  other: {
    id: "other",
    label: "OTHER",
    textColorVar: "var(--glass-text-muted)",
    badgeClass: "glass-outcome-default",
  },
};

// Classifier — substring matches in priority order.
//
// Verified May 2, 2026 against the live Rayston status enum: the current
// 7 statuses (paid_in_full, closed_payment_plan, paying_installment, lost,
// no_show, rescheduled, unique) + 2 rare statuses (abandoned_payment_plan,
// restored_from_abandon) have NO substring overlaps with each other. Order
// here is defensive against future drift — e.g. if a new status like
// "paid_installment" were ever added, it would match both "paid" and
// "installment" substrings; checking "installment" first preserves the
// installment bucket. Same logic for "restored" before "abandoned".
//
// JS substring fact-check (don't be fooled by visual similarity):
//   "abandoned_payment_plan".includes("paid")  → false  (payment ≠ paid)
//   "paying_installment".includes("paid")      → false  (paying ≠ paid)
//   "closed_payment_plan".includes("closed")   → true   (correct: closed bucket)
//   "restored_from_abandon".includes("abandoned") → false (abandon ≠ abandoned)
//   "pending_close".includes("closed")         → false  (it's "close", no 'd')
//     → pending_close historically fell through to "other"; now routed to its
//     own "pending" bucket (Mako Jun 20). The "pending" check sits BEFORE the
//     "closed" check defensively, in case a "pending_closed" status ever appears.
export function classifyOutcome(rawOutcome: string): OutcomeBucketId {
  const o = (rawOutcome || "").toLowerCase().trim();
  if (!o) return "other";
  if (o.includes("restored")) return "restored";
  if (o.includes("abandoned")) return "abandoned";
  if (o.includes("superseded")) return "other"; // pending_superseded — resolved dup, normally suppressed; never bucket as pending
  if (o.includes("pending")) return "pending"; // pending_close — provisional close, cash not in yet
  if (o.includes("unique")) return "unique"; // bespoke / one-off outcome
  if (o.includes("installment")) return "installment";
  if (o.includes("paid") || o.includes("closed") || o.includes("sale") || o.includes("deposit")) {
    return "closed";
  }
  if (o.includes("rescheduled")) return "rescheduled";
  if (o.includes("no show") || o.includes("noshow") || o === "no_show") return "noshow";
  if (o.includes("lost") || o.includes("cancel")) return "lost";
  return "other";
}
